import React from 'react';
import { formatTimestamp } from '../../utils/formatters';
import {
  getSentimentColor,
  getSentimentIcon,
} from '../../utils/utteranceRendering';
import { TranscriptUtterance, Speaker } from '../../types';
import EditorUtteranceSpeaker from './EditorUtteranceSpeaker';

interface EditorUtteranceMetaProps {
  utterance: TranscriptUtterance;
  speakers: Speaker[];
  onSpeakerChange: (utteranceId: string, speakerId: string) => void;
  onUtteranceClick: (e: React.MouseEvent, utteranceId: string) => void;
  horizontal?: boolean;
}

const EditorUtteranceMeta: React.FC<EditorUtteranceMetaProps> = ({
  utterance,
  speakers,
  onSpeakerChange,
  onUtteranceClick,
  horizontal = false,
}) => {
  return (
    <div className={horizontal ? 'flex items-center space-x-4' : 'space-y-3'}>
      <div className="text-base">
        <EditorUtteranceSpeaker
          utteranceId={utterance.id}
          speakerId={utterance.speakerId}
          speakers={speakers}
          onSpeakerChange={onSpeakerChange}
        />
      </div>

      <div className="text-xs">
        <button
          onClick={(e) => onUtteranceClick(e, utterance.id)}
          className="text-sm text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-mono"
          title="Jump to this timestamp"
        >
          {formatTimestamp(utterance.startTime)}
        </button>
      </div>

      {utterance.sentiment && (
        <div className="text-xs">
          <div className="flex items-center space-x-1">
            <span
              className="text-sm"
              title={`Sentiment: ${utterance.sentiment}`}
            >
              {getSentimentIcon(utterance.sentiment)}
            </span>
            <span
              className={`text-xs font-mono ${getSentimentColor(utterance.sentiment)}`}
              title={`Sentiment confidence: ${utterance.sentimentConfidence ? Math.round(utterance.sentimentConfidence * 100) : 0}%`}
            >
              {utterance.sentiment}
            </span>
          </div>
        </div>
      )}

      {process.env.REACT_APP_DEBUG_UTTERANCE_META === 'true' && (
        <div className="text-xs">
          <div
            className={`${horizontal ? 'flex space-x-1' : 'space-y-1'} text-xs text-gray-500 dark:text-gray-400 font-mono border border-gray-300 dark:border-gray-600 p-1 rounded`}
          >
            <div className="border border-gray-300 dark:border-gray-600 px-1 rounded">
              Pos: {utterance.position}
            </div>
            <div className="border border-gray-300 dark:border-gray-600 px-1 rounded">
              ID: {utterance.id}
            </div>
            <div className="border border-gray-300 dark:border-gray-600 px-1 rounded">
              Conf: {Math.round(utterance.confidenceScore * 100)}%
            </div>
            {utterance.sentimentConfidence && (
              <div className="border border-gray-300 dark:border-gray-600 px-1 rounded">
                Sent: {Math.round(utterance.sentimentConfidence * 100)}%
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorUtteranceMeta;
